import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ArrowLeft, Baby, Calendar, TrendingUp, BarChart3, MessageCircle, Clock } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const ChildProfile = () => {
  const { childId } = useParams();
  const [child, setChild] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChildData();
  }, [childId]);

  const fetchChildData = async () => {
    try {
      // Fetch all children to find the current one
      const childrenResponse = await axios.get(`${API_BASE}/children`);
      const currentChild = childrenResponse.data.find(c => c.id === childId);
      setChild(currentChild);

      // Fetch activities for this child
      const activitiesResponse = await axios.get(`${API_BASE}/activities/${childId}`);
      setActivities(activitiesResponse.data.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (error) {
      console.error('Failed to fetch child data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (birthDate) => {
    const birth = new Date(birthDate);
    const today = new Date();
    const diffTime = Math.abs(today - birth);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const ageInMonths = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
    
    if (ageInMonths < 1) {
      return `${Math.floor(diffDays / 7)} weeks`;
    } else if (ageInMonths < 12) {
      return `${ageInMonths} months`;
    } else {
      const years = Math.floor(ageInMonths / 12);
      const months = ageInMonths % 12;
      return months > 0 ? `${years}y ${months}m` : `${years} years`;
    }
  };

  const getExactAge = (birthDate) => {
    const birth = new Date(birthDate);
    const today = new Date();
    const diffTime = Math.abs(today - birth);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getMilestoneStats = () => {
    const milestoneTypes = {};
    activities.forEach(activity => {
      if (activity.milestone_category) {
        const category = activity.milestone_category.split('.')[0].trim();
        milestoneTypes[category] = (milestoneTypes[category] || 0) + 1;
      }
    });
    return Object.entries(milestoneTypes).sort(([,a], [,b]) => b - a);
  };

  const getRecentActivities = (count = 3) => {
    return activities.slice(0, count);
  };

  const getMilestoneColor = (category) => {
    const colors = {
      'Motor': 'bg-blue-100 text-blue-700 border-blue-200',
      'Cognitive': 'bg-purple-100 text-purple-700 border-purple-200',
      'Language': 'bg-green-100 text-green-700 border-green-200',
      'Social': 'bg-pink-100 text-pink-700 border-pink-200',
      'Emotional': 'bg-amber-100 text-amber-700 border-amber-200',
      'Physical': 'bg-red-100 text-red-700 border-red-200',
      'General Development': 'bg-slate-100 text-slate-700 border-slate-200'
    };
    
    const matchedCategory = Object.keys(colors).find(key => 
      category && category.toLowerCase().includes(key.toLowerCase())
    );
    
    return colors[matchedCategory] || colors['General Development'];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Child not found</p>
          <Link to="/dashboard">
            <Button className="gradient-emerald text-white">Go to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  const milestoneStats = getMilestoneStats();
  const recentActivities = getRecentActivities();
  const exactAge = getExactAge(child.birth_date);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-4 lg:p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8">
          <Link to="/dashboard">
            <Button variant="outline" size="sm" className="btn-hover">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl lg:text-4xl font-bold font-display bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              {child.name}'s Profile
            </h1>
            <p className="text-slate-600 mt-1">
              Comprehensive development overview and insights
            </p>
          </div>
        </div>

        {/* Child Profile Card */}
        <Card className="glass border-0">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-8">
              <div className="w-32 h-32 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center shadow-2xl">
                <Baby className="w-16 h-16 text-white" />
              </div>
              
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-4xl font-bold text-slate-800 mb-2">{child.name}</h2>
                <div className="space-y-2">
                  <div className="flex items-center justify-center md:justify-start space-x-2">
                    <Calendar className="w-5 h-5 text-slate-500" />
                    <span className="text-slate-600">
                      Born {new Date(child.birth_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-center md:justify-start space-x-4">
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 px-3 py-1">
                      {calculateAge(child.birth_date)} old
                    </Badge>
                    <Badge className="bg-teal-100 text-teal-700 border-teal-200 px-3 py-1">
                      {child.gender}
                    </Badge>
                    <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200 px-3 py-1">
                      {exactAge} days old
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col space-y-3">
                <Link to={`/activities/${child.id}`}>
                  <Button className="btn-hover gradient-emerald text-white w-full" data-testid="view-activities-button">
                    <Calendar className="w-4 h-4 mr-2" />
                    View Activities
                  </Button>
                </Link>
                <Link to={`/reports/${child.id}`}>
                  <Button className="btn-hover gradient-teal text-white w-full" data-testid="view-reports-button">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Weekly Reports
                  </Button>
                </Link>
                <Link to="/chat">
                  <Button variant="outline" className="btn-hover w-full" data-testid="chat-assistant-button">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Ask AI Assistant
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Development Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="glass border-0">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">{activities.length}</div>
              <p className="text-slate-600">Total Activities</p>
            </CardContent>
          </Card>
          
          <Card className="glass border-0">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-teal-600 mb-2">{milestoneStats.length}</div>
              <p className="text-slate-600">Milestone Types</p>
            </CardContent>
          </Card>
          
          <Card className="glass border-0">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-cyan-600 mb-2">
                {activities.filter(a => a.ai_analysis && a.ai_analysis !== 'AI analysis unavailable').length}
              </div>
              <p className="text-slate-600">AI Analyzed</p>
            </CardContent>
          </Card>
          
          <Card className="glass border-0">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-indigo-600 mb-2">
                {Math.ceil(exactAge / 7)}
              </div>
              <p className="text-slate-600">Weeks Old</p>
            </CardContent>
          </Card>
        </div>

        {/* Milestone Categories */}
        {milestoneStats.length > 0 && (
          <Card className="glass border-0">
            <CardHeader>
              <CardTitle className="font-display text-2xl text-slate-800 flex items-center space-x-2">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
                <span>Development Areas</span>
              </CardTitle>
              <CardDescription>
                Breakdown of {child.name}'s recorded milestones by category
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="milestone-categories">
                {milestoneStats.map(([category, count]) => (
                  <div 
                    key={category}
                    className="p-4 bg-white/50 rounded-lg border border-emerald-200/50 text-center card-hover"
                    data-testid={`milestone-stat-${category}`}
                  >
                    <div className="text-2xl font-bold text-slate-800 mb-2">{count}</div>
                    <Badge className={`${getMilestoneColor(category)} text-xs`}>
                      {category}
                    </Badge>
                    <p className="text-xs text-slate-500 mt-2">
                      {count === 1 ? 'milestone' : 'milestones'}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Activities */}
        <Card className="glass border-0">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-display text-2xl text-slate-800 flex items-center space-x-2">
                <Clock className="w-6 h-6 text-teal-600" />
                <span>Recent Activities</span>
              </CardTitle>
              <CardDescription>
                Latest recorded milestones and developments
              </CardDescription>
            </div>
            <Link to={`/activities/${child.id}`}>
              <Button variant="outline" className="btn-hover">
                View All
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentActivities.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No activities recorded yet</p>
                <Link to={`/activities/${child.id}`}>
                  <Button className="btn-hover gradient-emerald text-white">
                    <Calendar className="w-4 h-4 mr-2" />
                    Record First Activity
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4" data-testid="recent-activities">
                {recentActivities.map((activity) => (
                  <div 
                    key={activity.id} 
                    className="p-4 bg-white/50 rounded-lg border border-emerald-200/50 card-hover"
                    data-testid={`recent-activity-${activity.id}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-slate-800 flex-1">{activity.description}</h3>
                      <span className="text-sm text-slate-500 ml-4">
                        {new Date(activity.date).toLocaleDateString()}
                      </span>
                    </div>
                    
                    {activity.milestone_category && (
                      <div className="mb-2">
                        <Badge className={`${getMilestoneColor(activity.milestone_category)} text-xs`}>
                          {activity.milestone_category}
                        </Badge>
                      </div>
                    )}
                    
                    {activity.ai_analysis && activity.ai_analysis !== 'AI analysis unavailable' && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded p-3 mt-3">
                        <p className="text-sm text-emerald-700 leading-relaxed">
                          <span className="font-medium">AI Insight:</span> {activity.ai_analysis}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Development Timeline */}
        <Card className="glass border-0">
          <CardHeader>
            <CardTitle className="font-display text-2xl text-slate-800">Development Timeline</CardTitle>
            <CardDescription>
              {child.name}'s journey from birth to now
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Birth */}
              <div className="flex items-center space-x-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
                  <Baby className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Birth</h3>
                  <p className="text-sm text-slate-600">
                    {new Date(child.birth_date).toLocaleDateString()} • Welcome to the world, {child.name}!
                  </p>
                </div>
              </div>
              
              {/* Current Age */}
              <div className="flex items-center space-x-4 p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-200">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Today</h3>
                  <p className="text-sm text-slate-600">
                    {calculateAge(child.birth_date)} • {activities.length} activities recorded
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChildProfile;
